import { useSetConfirm } from "@/shared/atoms/useConfirm";
import useOnClickOutside from "@/shared/hooks/useOnClickOutside";
import { cn } from "@/shared/utils/cn";
import { MouseEvent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { actionMessages } from "../../shared/utils/constant";
import Icon, { IconsVariant } from "../Icon/Icon";
import IconToolTip from "../Icon/IconToolTip";
import useSideBarAtom from "@/shared/atoms/useSideBarAtom";

const actionBarShadow = "shadow-[0px_0.4rem_0.5rem_0px_rgba(0,0,0,0.2)]";
const menuRowClass =
  "cursor-pointer border-t border-light p-[2rem] transition-all hover:bg-light";

export interface TableActionProps {
  onVisibleClick?: () => void;
  onHiddenClick?: () => void;
  onFavouriteClick?: () => void;
  onDeFavouriteClick?: () => void;
  onActiveClick?: () => void;
  onDeActiveClick?: () => void;
  onTrashClick?: (e?: MouseEvent<HTMLDivElement>) => void;
  onDeTrashClick?: (e?: MouseEvent<HTMLDivElement>) => void;
  show?: boolean;
  ids: number[];
  customActions?: {
    click: (ids: number[]) => void;
    icon?: IconsVariant;
    tooltipMessage?: string;
    children?: React.ReactNode;
  }[];
}

const TableAction = ({
  show = false,
  onVisibleClick,
  onHiddenClick,
  onFavouriteClick,
  onDeFavouriteClick,
  onDeTrashClick,
  onTrashClick,
  onActiveClick,
  onDeActiveClick,
  ids,
  customActions,
}: TableActionProps) => {
  const [{ isCollapsed }] = useSideBarAtom();
  const [drop, setDrop] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useOnClickOutside(ref, () => setDrop(false));
  const { t } = useTranslation();
  const confirm = useSetConfirm();

  const handleDelete = () => {
    confirm({ message: t("confirmationMessage"), cb: () => onTrashClick() });
  };

  return (
    <div
      ref={ref}
      className={cn(
        "fixed top-[5.6rem] z-10 bg-white transition-all md:top-[7.92rem]",
        actionBarShadow,
        isCollapsed
          ? "left-30 w-[calc(100%-7.5rem)]"
          : "left-108 w-[calc(100%-27rem)]",
        show ? "visible opacity-100" : "invisible opacity-0"
      )}
    >
      <div className="flex items-center px-8 py-5">
        <div className="hidden md:flex">
          {onTrashClick !== undefined && (
            <div
              onClick={() => handleDelete()}
              className="mr-4 flex items-center"
            >
              <IconToolTip
                text={t(`table_actions.${actionMessages.trash}`)}
                variants="trash"
              />
            </div>
          )}
          {onFavouriteClick !== undefined && (
            <div
              onClick={() => onFavouriteClick()}
              className="mr-4 flex items-center"
            >
              <IconToolTip
                text={t(`table_actions.${actionMessages.favourite}`)}
                variants="starsolid"
              />
            </div>
          )}
          {onDeFavouriteClick !== undefined && (
            <div
              onClick={() => onDeFavouriteClick()}
              className="mr-4 flex items-center"
            >
              <IconToolTip
                text={t(`table_actions.${actionMessages.defavourite}`)}
                variants="starregular"
              />
            </div>
          )}
          {onVisibleClick !== undefined && (
            <div
              onClick={() => onVisibleClick()}
              className="mr-4 flex items-center"
            >
              <IconToolTip
                text={t(`table_actions.${actionMessages.visible}`)}
                variants="eye"
              />
            </div>
          )}
          {onHiddenClick !== undefined && (
            <div
              onClick={() => onHiddenClick()}
              className="mr-4 flex items-center"
            >
              <IconToolTip
                text={t(`table_actions.${actionMessages.hide}`)}
                variants="eyeslash"
              />
            </div>
          )}
          {onActiveClick !== undefined && (
            <div
              onClick={() => onActiveClick()}
              className="mr-4 flex items-center justify-center"
            >
              <IconToolTip
                text={t(`table_actions.${actionMessages.active}`)}
                variants="check"
              />
            </div>
          )}
          {onDeActiveClick !== undefined && (
            <div
              onClick={() => onDeActiveClick()}
              className="mr-4 flex items-center justify-center"
            >
              <IconToolTip
                text={t(`table_actions.${actionMessages.deactive}`)}
                variants="times"
              />
            </div>
          )}
          {customActions?.map((action, index) => (
            <div
              onClick={() => action.click(ids)}
              key={index}
              className="mr-4 flex items-center justify-center"
            >
              {action.children || (
                <IconToolTip
                  text={action.tooltipMessage}
                  variants={action.icon}
                />
              )}
            </div>
          ))}
        </div>

        <div
          onClick={() => setDrop(!drop)}
          className="ml-auto flex cursor-pointer items-center"
        >
          <div className="mr-[1rem] font-bold">Meer opties</div>
          <Icon width="0.87rem" variants="ellipsis" fill="text" />
        </div>
      </div>
      <div
        className={cn(
          "absolute w-full overflow-auto bg-white transition-all md:right-0 md:w-120",
          actionBarShadow,
          "max-h-[calc(100vh-11.37rem)]",
          drop ? "visible opacity-100" : "invisible opacity-0"
        )}
      >
        {onFavouriteClick && (
          <div className={menuRowClass} onClick={() => onFavouriteClick()}>
            {t(`table_actions.${actionMessages.favourite}`)}
          </div>
        )}
        {onDeFavouriteClick && (
          <div className={menuRowClass} onClick={() => onDeFavouriteClick()}>
            {t(`table_actions.${actionMessages.defavourite}`)}
          </div>
        )}
        {onVisibleClick && (
          <div className={menuRowClass} onClick={() => onVisibleClick()}>
            {t(`table_actions.${actionMessages.visible}`)}
          </div>
        )}
        {onHiddenClick && (
          <div className={menuRowClass} onClick={() => onHiddenClick()}>
            {t(`table_actions.${actionMessages.hide}`)}
          </div>
        )}
        {onActiveClick && (
          <div className={menuRowClass} onClick={() => onActiveClick()}>
            {t(`table_actions.${actionMessages.active}`)}
          </div>
        )}
        {onDeActiveClick && (
          <div className={menuRowClass} onClick={() => onDeActiveClick()}>
            {t(`table_actions.${actionMessages.deactive}`)}
          </div>
        )}
        {onTrashClick && (
          <div className={menuRowClass} onClick={() => onTrashClick()}>
            {t(`table_actions.${actionMessages.trash}`)}
          </div>
        )}
        {onDeTrashClick && (
          <div className={menuRowClass} onClick={() => onDeTrashClick()}>
            {t(`table_actions.${actionMessages.detrash}`)}
          </div>
        )}
      </div>
    </div>
  );
};

export default TableAction;
